'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const scanHistory = [
{ id: 'scan-001', location: 'BGC, Taguig', time: 'Today, 2:34 PM', lat: '14.5547', lng: '121.0244', isLatest: true },
{ id: 'scan-002', location: 'Makati City', time: 'Jul 20, 2026 · 10:12 AM', lat: '14.5547', lng: '121.0244', isLatest: false },
{ id: 'scan-003', location: 'Quezon City', time: 'Jul 15, 2026 · 4:55 PM', lat: '14.6760', lng: '121.0437', isLatest: false }];


export default function NFCTagScanShell() {
  const [lostMode, setLostMode] = useState(false);
  const [locationShared, setLocationShared] = useState(false);
  const [foundSubmitted, setFoundSubmitted] = useState(false);

  const handleShareLocation = () => {
    // In production: capture geolocation and send to owner via backend
    setLocationShared(true);
  };

  const handleFoundPet = () => {
    // Backend integration point: POST /api/tags/found { tagId, location, finderContact }
    setFoundSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#F6F7F4] flex flex-col items-center py-6 px-4">
      {/* Mobile frame hint */}
      <div className="w-full max-w-[390px] flex flex-col gap-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 no-underline">
            <AppLogo size={32} />
            <span className="font-display text-lg font-extrabold">
              <span className="text-navy">Pet</span>
              <span className="text-brand-red">Hub</span>
              <span className="text-xs font-bold text-muted-foreground ml-1">PH</span>
            </span>
          </Link>
          <div className="flex items-center gap-1.5 bg-secondary-tint text-secondary text-xs font-bold px-3 py-1.5 rounded-full">
            <div className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse"></div>
            Tap or Scan Active
          </div>
        </div>

        {/* LOST MODE BANNER */}
        {lostMode &&
        <div className="lost-pulse bg-brand-red rounded-2xl p-4 text-white fade-in">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ExclamationTriangleIcon" size={20} className="text-white flex-shrink-0" />
              <h2 className="text-base font-extrabold tracking-wide">⚠ THIS PET IS MISSING</h2>
            </div>
            <p className="text-sm font-medium text-white/90 leading-relaxed">
              Last seen: BGC, Taguig · Jul 27, 2026 at 11:30 AM
            </p>
            <p className="text-sm font-bold text-yellow mt-1">
              🏆 Reward offered for safe return
            </p>
          </div>
        }

        {/* Safe mode banner */}
        {!lostMode &&
        <div className="bg-secondary-tint border border-secondary/25 rounded-2xl px-4 py-3 flex items-center gap-3 fade-in">
            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
              <Icon name="HomeIcon" size={16} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-secondary">Luna is safe at home 🏠</p>
              <p className="text-xs text-body-text font-medium">Owner has been notified of this scan</p>
            </div>
          </div>
        }

        {/* Pet card */}
        <div className="bg-white rounded-2xl border border-border/60 shadow-card overflow-hidden">
          {/* Pet photo */}
          <div className="relative h-[260px] bg-muted">
            <AppImage
              src="https://images.unsplash.com/photo-1611786896477-2e4a99745038"
              alt="Luna — white and brown Shih Tzu dog with fluffy fur and big dark eyes"
              fill
              className="object-cover"
              priority />
            
            {/* Tag ID pill */}
            <div className="absolute bottom-3 left-3 bg-navy/85 text-white text-xs font-extrabold px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5">
              <Icon name="WifiIcon" size={12} className="text-yellow" />
              NFC-PH-00847
            </div>
            {/* Lost mode overlay */}
            {lostMode &&
            <div className="absolute inset-0 bg-brand-red/20 flex items-end justify-center pb-16">
                <div className="bg-brand-red text-white text-sm font-extrabold px-5 py-2 rounded-full animate-bounce">
                  MISSING
                </div>
              </div>
            }
          </div>

          {/* Pet info */}
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h1 className="text-xl font-extrabold text-foreground">Luna</h1>
                <p className="text-sm text-body-text font-medium">Shih Tzu · Female · 3 years old</p>
              </div>
              <Link
                href="/pet-profile"
                className="text-xs font-bold text-primary hover:text-primary-hover transition-colors no-underline flex items-center gap-1">
                
                Full Profile
                <Icon name="ArrowTopRightOnSquareIcon" size={12} />
              </Link>
            </div>

            {/* Info tiles */}
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground font-semibold mb-0.5">Microchip</p>
                <p className="text-sm font-bold text-foreground font-tabular">PHL-••••-4782</p>
              </div>
              <div className="bg-muted rounded-xl p-3">
                <p className="text-xs text-muted-foreground font-semibold mb-0.5">Veterinarian</p>
                <p className="text-sm font-bold text-foreground">Dr. Ana Reyes</p>
              </div>
              <div className="bg-accent-tint border border-accent/20 rounded-xl p-3">
                <p className="text-xs text-accent-dark font-semibold mb-0.5 flex items-center gap-1">
                  <Icon name="ExclamationTriangleIcon" size={11} />
                  Allergies
                </p>
                <p className="text-sm font-bold text-accent-dark">Chicken protein</p>
              </div>
              <div className="bg-primary-tint rounded-xl p-3">
                <p className="text-xs text-muted-foreground font-semibold mb-0.5">Medication</p>
                <p className="text-sm font-bold text-primary">Nexgard monthly</p>
              </div>
            </div>

            {/* Emergency instructions */}
            <div className="bg-primary-tint border border-primary/15 rounded-xl p-3.5 mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Icon name="InformationCircleIcon" size={15} className="text-primary flex-shrink-0" />
                <p className="text-xs font-bold text-primary">If you found this pet</p>
              </div>
              <p className="text-xs text-body-text font-medium leading-relaxed">
                Please keep Luna safe and calm. Tap "I Found This Pet" below to notify the owner instantly with your location. Do not feed unfamiliar food due to allergies.
              </p>
            </div>

            {/* LOST MODE ACTIONS */}
            {lostMode && !foundSubmitted &&
            <div className="flex flex-col gap-2.5 fade-in">
                <a
                href="tel:+639XXXXXXXXX"
                className="flex items-center justify-center gap-2 w-full bg-brand-red hover:bg-brand-red-hover text-white font-bold text-sm py-4 rounded-full transition-colors shadow-red-glow"
                // Backend integration point: masked phone number from owner profile
              >
                  <Icon name="PhoneIcon" size={18} />
                  Call Owner (Number Masked)
                </a>
                <button className="flex items-center justify-center gap-2 w-full btn-outline text-sm py-3">
                  <Icon name="ChatBubbleLeftRightIcon" size={16} />
                  Message via PetHub
                </button>
                <button
                onClick={handleShareLocation}
                className={`flex items-center justify-center gap-2 w-full text-sm py-3 rounded-full font-bold transition-colors ${
                locationShared ?
                'bg-secondary-tint text-secondary border border-secondary/30' : 'bg-muted text-body-text hover:bg-primary-tint hover:text-primary border border-border'}`
                }>
                
                  <Icon name="MapPinIcon" size={16} />
                  {locationShared ? '📍 Location Shared with Owner' : 'Share My Location'}
                </button>
                <button
                onClick={handleFoundPet}
                className="flex items-center justify-center gap-2 w-full bg-secondary hover:bg-secondary-hover text-white font-bold text-sm py-4 rounded-full transition-colors">
                
                  <Icon name="CheckCircleIcon" size={18} />
                  I Found This Pet 🐾
                </button>
                <p className="text-center text-xs text-muted-foreground font-medium">
                  Owner is notified instantly when you interact
                </p>
              </div>
            }

            {/* Found submitted state */}
            {lostMode && foundSubmitted &&
            <div className="bg-secondary-tint border border-secondary/25 rounded-xl p-4 text-center fade-in">
                <div className="text-3xl mb-2">🎉</div>
                <p className="text-sm font-bold text-secondary">Thank you for helping!</p>
                <p className="text-xs text-body-text font-medium mt-1">
                  Luna's owner has been notified. They will contact you shortly. You're a hero! 🐾
                </p>
              </div>
            }

            {/* SAFE MODE ACTIONS */}
            {!lostMode &&
            <div className="flex flex-col gap-2 fade-in">
                <button className="btn-outline text-sm py-3 w-full justify-center">
                  <Icon name="ChatBubbleLeftRightIcon" size={16} />
                  Message Owner
                </button>
                <Link href="/sign-up-login-screen" className="text-center text-xs text-primary font-semibold hover:underline no-underline block mt-1">
                  Register your own pet on PetHub PH →
                </Link>
              </div>
            }
          </div>
        </div>

        {/* Owner control card */}
        <div className="bg-white rounded-2xl border border-border/60 shadow-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-bold text-foreground">Lost Mode</p>
              <p className="text-xs text-muted-foreground font-medium">
                {lostMode ? 'Active — finder actions shown' : 'Off — pet is safe at home'}
              </p>
            </div>
            <label className="toggle-switch" aria-label="Toggle Lost Mode">
              <input
                type="checkbox"
                checked={lostMode}
                onChange={() => {setLostMode(!lostMode);setFoundSubmitted(false);setLocationShared(false);}} />
              
              <span className="toggle-slider"></span>
            </label>
          </div>
          <p className="text-xs text-muted-foreground font-medium">
            {lostMode ?
            '⚠ Lost Mode is ON. Finders will see emergency contact options.' : '✓ Owner controls Lost Mode from their PetHub account.'}
          </p>
        </div>

        {/* Scan history */}
        <div className="bg-white rounded-2xl border border-border/60 shadow-card p-4">
          <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
            <Icon name="ClockIcon" size={15} className="text-muted-foreground" />
            Scan History
          </h3>
          <div className="flex flex-col gap-2">
            {scanHistory?.map((scan) =>
            <div
              key={scan?.id}
              className={`flex items-center gap-3 p-3 rounded-xl ${scan?.isLatest ? 'bg-primary-tint border border-primary/20' : 'bg-muted'}`}>
              
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${scan?.isLatest ? 'bg-primary' : 'bg-muted-foreground/40'}`}></div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-foreground">{scan?.location}</p>
                  <p className="text-xs text-muted-foreground font-medium">{scan?.time}</p>
                </div>
                {scan?.isLatest &&
              <span className="text-xs font-bold text-primary bg-white px-2 py-0.5 rounded-full flex-shrink-0">Latest</span>
              }
              </div>
            )}
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center py-4">
          <p className="text-xs text-muted-foreground font-medium">
            Works with NFC tap or QR scan · no app required
          </p>
          <Link href="/" className="text-xs text-primary font-bold hover:underline no-underline mt-1 block">
            Register your pet on PetHub PH — it's free 🐾
          </Link>
        </div>
      </div>
    </div>);

}